import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    ToggleButton,
    ToggleButtonGroup,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { useState, useEffect } from "react";
import { GridConfig } from "./TaxReturnWebforms/types/formConfig";
import Button from "../atoms/Button";

interface GridEditDialogProps {
    grid: GridConfig | null;
    open: boolean;
    onClose: () => void;
    onSave: (updated: GridConfig) => void;
    onDelete?: () => void;
    canDelete: boolean;
    isColumnarSection?: boolean; // Parent section has columnar-properties layout
}

type ColumnsMode = 'simple' | 'responsive';

const GridEditDialog = ({
    grid,
    open,
    onClose,
    onSave,
    onDelete,
    canDelete,
    isColumnarSection = false,
}: GridEditDialogProps) => {
    const [columnsMode, setColumnsMode] = useState<ColumnsMode>('simple');
    const [simpleColumns, setSimpleColumns] = useState<number>(2);
    const [responsiveColumns, setResponsiveColumns] = useState<{
        xs?: number;
        sm?: number;
        md?: number;
        lg?: number;
    }>({});
    const [gap, setGap] = useState<number>(2);
    const [rowLabel, setRowLabel] = useState<string>("");
    const [position, setPosition] = useState<'' | 'header' | 'footer'>('');

    useEffect(() => {
        if (grid) {
            // Determine columns mode
            if (typeof grid.columns === 'number') {
                setColumnsMode('simple');
                setSimpleColumns(grid.columns);
                setResponsiveColumns({});
            } else if (grid.columns && typeof grid.columns === 'object') {
                setColumnsMode('responsive');
                setResponsiveColumns(grid.columns);
                setSimpleColumns(2);
            } else {
                setColumnsMode('simple');
                setSimpleColumns(2);
                setResponsiveColumns({});
            }
            setGap(grid.gap ?? 2);
            setRowLabel(grid.rowLabel || "");
            setPosition(grid.position || '');
        }
    }, [grid]);

    const handleSave = () => {
        if (!grid) return;

        const columns = columnsMode === 'simple'
            ? simpleColumns
            : Object.keys(responsiveColumns).length > 0
                ? responsiveColumns
                : undefined;

        const updated: GridConfig = {
            ...grid,
            columns,
            gap: gap !== 2 ? gap : undefined,
            rowLabel: isColumnarSection && rowLabel ? rowLabel : undefined,
            position: isColumnarSection && position ? position : undefined,
        };

        // Clean up undefined values
        if (!updated.columns) delete updated.columns;
        if (updated.gap === undefined) delete updated.gap;
        if (!updated.rowLabel) delete updated.rowLabel;
        if (!updated.position) delete updated.position;

        onSave(updated);
        onClose();
    };

    const handleDelete = () => {
        if (onDelete) {
            onDelete();
            onClose();
        }
    };

    const handleColumnsModeChange = (_: React.MouseEvent<HTMLElement>, newMode: ColumnsMode | null) => {
        if (newMode !== null) {
            setColumnsMode(newMode);
        }
    };

    const handleResponsiveColumnChange = (breakpoint: 'xs' | 'sm' | 'md' | 'lg', value: string) => {
        const numValue = parseInt(value, 10);
        setResponsiveColumns((prev) => {
            const newColumns = { ...prev };
            if (value === '' || Number.isNaN(numValue)) {
                delete newColumns[breakpoint];
            } else {
                newColumns[breakpoint] = numValue;
            }
            return newColumns;
        });
    };

    if (!grid) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                Edit Grid
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    {/* Columns mode toggle */}
                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Columns Mode
                        </Typography>
                        <ToggleButtonGroup
                            value={columnsMode}
                            exclusive
                            onChange={handleColumnsModeChange}
                            size="small"
                            fullWidth
                        >
                            <ToggleButton value="simple">
                                Simple (Number)
                            </ToggleButton>
                            <ToggleButton value="responsive">
                                Responsive (Breakpoints)
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    {/* Simple columns input */}
                    {columnsMode === 'simple' && (
                        <TextField
                            label="Columns"
                            type="number"
                            value={simpleColumns}
                            onChange={(e) => setSimpleColumns(parseInt(e.target.value, 10) || 1)}
                            fullWidth
                            size="small"
                            inputProps={{ min: 1, max: 12 }}
                            helperText="Number of columns in the grid (1-12)"
                        />
                    )}

                    {/* Responsive columns inputs */}
                    {columnsMode === 'responsive' && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                Responsive Columns (leave empty to skip breakpoint)
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
                                <TextField
                                    label="xs (extra small)"
                                    type="number"
                                    value={responsiveColumns.xs ?? ''}
                                    onChange={(e) => handleResponsiveColumnChange('xs', e.target.value)}
                                    size="small"
                                    inputProps={{ min: 1, max: 12 }}
                                />
                                <TextField
                                    label="sm (small)"
                                    type="number"
                                    value={responsiveColumns.sm ?? ''}
                                    onChange={(e) => handleResponsiveColumnChange('sm', e.target.value)}
                                    size="small"
                                    inputProps={{ min: 1, max: 12 }}
                                />
                                <TextField
                                    label="md (medium)"
                                    type="number"
                                    value={responsiveColumns.md ?? ''}
                                    onChange={(e) => handleResponsiveColumnChange('md', e.target.value)}
                                    size="small"
                                    inputProps={{ min: 1, max: 12 }}
                                />
                                <TextField
                                    label="lg (large)"
                                    type="number"
                                    value={responsiveColumns.lg ?? ''}
                                    onChange={(e) => handleResponsiveColumnChange('lg', e.target.value)}
                                    size="small"
                                    inputProps={{ min: 1, max: 12 }}
                                />
                            </Box>
                        </Box>
                    )}

                    {/* Gap input */}
                    <TextField
                        label="Gap"
                        type="number"
                        value={gap}
                        onChange={(e) => setGap(parseInt(e.target.value, 10) || 0)}
                        fullWidth
                        size="small"
                        inputProps={{ min: 0, max: 8 }}
                        helperText="Spacing between grid items (MUI spacing units, default: 2)"
                    />

                    {/* Columnar-specific fields */}
                    {isColumnarSection && (
                        <>
                            <TextField
                                label="Row Label"
                                value={rowLabel}
                                onChange={(e) => setRowLabel(e.target.value)}
                                fullWidth
                                size="small"
                                placeholder='e.g., "Line 3: Rents received"'
                                helperText="Label for this row in the columnar table"
                            />

                            <FormControl fullWidth size="small">
                                <InputLabel>Position</InputLabel>
                                <Select
                                    value={position}
                                    label="Position"
                                    onChange={(e) => setPosition(e.target.value as '' | 'header' | 'footer')}
                                >
                                    <MenuItem value="">None (Row)</MenuItem>
                                    <MenuItem value="header">Header</MenuItem>
                                    <MenuItem value="footer">Footer</MenuItem>
                                </Select>
                            </FormControl>
                        </>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
                <Box>
                    {canDelete && onDelete && (
                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={handleDelete}
                        >
                            Delete Grid
                        </Button>
                    )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="outlined" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleSave}>
                        Save
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default GridEditDialog;
